import type { Supplier, SupplierDocumentType } from "@fiscalo/shared-types";

import { apiClient } from "@/services/apiClient";

export type { Supplier, SupplierDocumentType };

export interface ListSuppliersParams {
  page?: number;
  limit?: number;
  search?: string;
  documentType?: SupplierDocumentType;
  autoAckCfeOnReceive?: boolean;
}

export interface ListSuppliersResponse {
  data: Supplier[];
  total: number;
}

export interface CreateSupplierPayload {
  legalName: string;
  displayName?: string;
  documentType: SupplierDocumentType;
  rut?: string;
  countryCode?: string;
  docRecepExt?: string;
  address?: string;
  ciudad?: string;
  departamento?: string;
  email?: string;
  phone?: string;
  defaultCurrency?: "USD" | "UYU";
  defaultCategory?: string;
  autoAckCfeOnReceive?: boolean;
  notes?: string;
}

export type UpdateSupplierPayload = Partial<CreateSupplierPayload>;

export interface SupplierRucInfoResponse {
  valid: boolean;
  legalName?: string;
  legalAddress?: string;
  city?: string;
  department?: string;
  phone?: string;
  email?: string;
  fechaInicio?: string;
}

export function getRucInfoForSupplier(
  rut: string,
): Promise<SupplierRucInfoResponse> {
  return apiClient
    .get<SupplierRucInfoResponse>("/suppliers/ruc-info", { params: { rut } })
    .then((res) => res.data);
}

export function listSuppliers(
  params: ListSuppliersParams = {},
): Promise<ListSuppliersResponse> {
  const { page = 1, limit = 10, search, documentType, autoAckCfeOnReceive } = params;
  const requestParams: Record<string, string | number | boolean> = { page, limit };
  if (search?.trim()) requestParams.search = search.trim();
  if (documentType) requestParams.documentType = documentType;
  if (typeof autoAckCfeOnReceive === "boolean")
    requestParams.autoAckCfeOnReceive = autoAckCfeOnReceive;
  return apiClient
    .get<ListSuppliersResponse>("/suppliers", { params: requestParams })
    .then((res) => res.data);
}

export function getSupplier(id: string): Promise<Supplier> {
  return apiClient.get<Supplier>(`/suppliers/${id}`).then((res) => res.data);
}

export function createSupplier(
  payload: CreateSupplierPayload,
): Promise<Supplier> {
  return apiClient
    .post<Supplier>("/suppliers", payload)
    .then((res) => res.data);
}

export function updateSupplier(
  id: string,
  payload: UpdateSupplierPayload,
): Promise<Supplier> {
  return apiClient
    .patch<Supplier>(`/suppliers/${id}`, payload)
    .then((res) => res.data);
}

export function deleteSupplier(id: string): Promise<void> {
  return apiClient.delete(`/suppliers/${id}`).then(() => undefined);
}

export interface SupplierMonthlyBucket {
  month: string;
  recibidasUyu: number;
  recibidasUsd: number;
  expensesUyu: number;
  expensesUsd: number;
  ivaCreditUyu: number;
  ivaCreditUsd: number;
}

export interface SupplierCategoryBreakdown {
  category: string;
  amountUyu: number;
  amountUsd: number;
  count: number;
}

export interface SupplierSpendSummary {
  supplierId: string;
  legalName: string;
  monthly: SupplierMonthlyBucket[];
  ytd: {
    recibidasUyu: number;
    recibidasUsd: number;
    expensesUyu: number;
    expensesUsd: number;
    ivaCreditUyu: number;
    ivaCreditUsd: number;
    recibidasCount: number;
    expensesCount: number;
  };
  allTime: {
    recibidasCount: number;
    expensesCount: number;
    firstRecibidaAt: string | null;
    lastRecibidaAt: string | null;
  };
  topCategoriesYtd: SupplierCategoryBreakdown[];
  pendingAckCount: number;
}

export function getSupplierSpendSummary(
  id: string,
): Promise<SupplierSpendSummary> {
  return apiClient
    .get<SupplierSpendSummary>(`/suppliers/${id}/spend-summary`)
    .then((res) => res.data);
}
